/** @jsx jsx */
import { jsx } from "@emotion/core"
import { Fragment } from "react"
import PropTypes from "prop-types"
import { Link } from "gatsby"
import SVGInline from "react-svg-inline"
import { Global, css } from "@emotion/core"

import ReadMore from "../../components/read-more-button"
import { getLink } from "../../utils/navigation"
import {
  howItWorksSVGStyles,
  withHowItWorksGridBorder,
  bodyCopy,
  howItWorksSVGHoverStyles,
} from "../../utils/styles"
import LinkWithArrow from "../../components/link-with-arrow"

const cardPadding = theme => theme.space[8]

const figureStyles = theme => ({
  color: theme.colors.blue[90],
  position: `absolute`,
  top: 0,
  left: 0,
  fontWeight: 400,
  fontFamily: theme.fonts.body,
  textTransform: `uppercase`,
  fontSize: theme.fontSizes[0],
  opacity: 0.3,
})

const styles = {
  defaults: theme => [
    {
      color: `${theme.colors.blue[90]} !important`,
      padding: cardPadding(theme),
      flex: `1 1 100%`,
    },
    withHowItWorksGridBorder(theme),
    {
      marginTop: -1,
      // reset withHowItWorksGridBorder for two columns
      [theme.mediaQueries.desktop]: {
        "&:after": {
          display: `none !important`,
        },
      },
    },
  ],
  cardHoverDesktopStyle: theme => ({
    "&:before": {
      background: theme.colors.white,
      content: `" "`,
      display: `block`,
      height: `100%`,
      left: 0,
      position: `absolute`,
      top: 0,
      bottom: 0,
      transform: `scaleY(0)`,
      transition: `transform ${theme.transitions.speed.default} ${theme.transitions.curve.default}`,
      transformOrigin: `center bottom`,
      width: `100%`,
      zIndex: -1,
    },
    "&:hover:before": {
      transform: `scaleY(1)`,
    },
    "&:hover h2": {
      color: `${theme.colors.gatsby} !important`,
    },
    "&:hover div": {
      color: `${theme.colors.purple[90]} !important`,
    },
    "&:hover .read-more": {
      background: theme.colors.gatsby,
      color: `${theme.colors.white} !important`,
      border: `1px solid ${theme.colors.gatsby} !important`,
    },
    "&:hover": {
      boxShadow: theme.shadows.dialog,
    },
  }),
}

/**
 * This component is aimed to display a "keyvisual" that can be added by content editors in Contentful
 * There are two ways to display those:
 *   - custom SVG which must exist in assets folder in this repo
 *   - image URL provided by Contentful (keyvisual.asset.file.url)
 *
 * We try to find a custom SVG first and gracefully fall back to the Contentful image if the SVG is not available
 */
function KeyVisual({ keyvisual, isHowItWorksListPage }) {
  let Visual = null

  try {
    Visual = keyvisual
      ? require(`!raw-loader!svgo-loader?{"plugins":[{"removeViewBox":false}]}!../../assets/${keyvisual.name}.svg`)
          .default
      : null
  } catch (e) {
    Visual = null
  }

  const containerCss = theme => ({
    flexShrink: 0,
    width: 120,
    height: 120,
    padding: isHowItWorksListPage ? 0 : 20,
    display: `flex`,
    alignItems: `center`,
    justifyContent: `center`,
    [theme.mediaQueries.desktop]: {
      width: 140,
      height: 140,
      marginRight: isHowItWorksListPage ? -20 : 0,
      padding: isHowItWorksListPage ? 0 : 40,
    },
    [theme.mediaQueries.hd]: {
      width: 160,
      height: 160,
    },
  })

  // A local SVG asset corresponding to the name specified in Contentful was found
  if (Visual) {
    return (
      <div
        css={theme => [
          containerCss(theme),
          {
            "& svg": [
              {
                width: `100%`,
                height: `100%`,
                position: `relative`,
                zIndex: 2,
                opacity: 0.5,
              },
              howItWorksSVGStyles(theme),
            ],
          },
        ]}
      >
        <SVGInline svg={Visual} />
      </div>
    )
  }

  const visualSrc = keyvisual?.asset?.file?.url

  if (!visualSrc) {
    return null
  }

  return (
    <div css={containerCss}>
      <img
        src={visualSrc}
        alt=""
        css={{
          opacity: 0.5,
          position: `relative`,
          zIndex: 2,
          margin: 0,
          maxHeight: `100%`,
        }}
      />
    </div>
  )
}

const Card = ({ section, index }) => {
  const { title, keyvisual, associatedPage, text } = section
  const isHowItWorksListPage =
    associatedPage &&
    associatedPage.parentPage &&
    associatedPage.parentPage.slug === `how-it-works`
  const isOdd = (index + 1) % 2 === 0

  return (
    <Fragment>
      <Global
        // couldn't make the counter prefix work inline, so let's go global
        styles={css`
          .how-it-works-card-title::after {
            content: "Fig. " counter(how-it-works-counter);
            counter-increment: how-it-works-counter;
          }
        `}
      />
      {associatedPage ? (
        <Link
          to={getLink(associatedPage)}
          css={theme => [
            styles.defaults(theme),
            styles.cardHoverDesktopStyle(theme),
            {
              marginLeft: `-${cardPadding(theme)}`,
              marginRight: `-${cardPadding(theme)}`,
              display: `flex`,
              flexDirection: `column`,
              position: `relative`,
              justifyContent: `flex-start`,
              textDecoration: `none`,
              transitionDuration: theme.transitions.speed.default,
              transitionProperty: `color, background-color, box-shadow, transform`,
              zIndex: `499`,
              "&:hover svg": [
                {
                  opacity: 1,
                },
                howItWorksSVGHoverStyles(theme),
              ],
              "&:hover img": [
                {
                  opacity: 1,
                },
                howItWorksSVGHoverStyles(theme),
              ],
            },
            {
              [theme.mediaQueries.desktop]: {
                marginLeft: isOdd ? 0 : `-${cardPadding(theme)}`,
                marginRight: !isOdd ? 0 : `-${cardPadding(theme)}`,
              },
            },
          ]}
        >
          <div
            css={{
              position: `relative`,
              display: `flex`,
              width: `100%`,
            }}
          >
            <h2
              className="how-it-works-card-title"
              css={theme => ({
                paddingTop: `${parseInt(cardPadding(theme), 10) * 1}rem`,
                flex: `1 1 auto`,
                position: `relative`,
                [theme.mediaQueries.phablet]: {
                  fontSize: theme.fontSizes[6],
                },
                [theme.mediaQueries.desktop]: {
                  fontSize: theme.fontSizes[5],
                },
                "&:after": figureStyles(theme),
              })}
            >
              <LinkWithArrow title={title} />
            </h2>
            <KeyVisual
              keyvisual={keyvisual}
              isHowItWorksListPage={isHowItWorksListPage}
            />
          </div>
          <div
            css={theme => ({
              [theme.mediaQueries.desktop]: { fontSize: theme.fontSizes[1] },
              color: theme.colors.grey[70],
            })}
          >
            <div
              css={{ maxWidth: 400 }}
              dangerouslySetInnerHTML={{
                __html: text && text.childMarkdownRemark.html,
              }}
            />
          </div>
          <div css={{ marginTop: `auto` }}>
            <ReadMore
              css={theme => ({
                background: theme.colors.whiteFade[80],
                border: `1px solid ${theme.colors.blue[10]}`,
                color: theme.colors.blue[90],
              })}
            />
          </div>
        </Link>
      ) : (
        <div
          css={theme => [
            styles.defaults(theme),
            {
              marginLeft: isOdd ? 0 : `-${cardPadding(theme)}`,
              marginRight: !isOdd ? 0 : `-${cardPadding(theme)}`,
            },
          ]}
        >
          <p
            css={theme => [
              figureStyles(theme),
              { left: cardPadding(theme), top: cardPadding(theme) },
            ]}
          >
            Orientation
          </p>
          <div
            css={theme => ({
              ...bodyCopy(theme),
              color: theme.colors.grey[70],
              position: `relative`,
              paddingTop: `${parseInt(cardPadding(theme), 10) * 1}rem`,
              [theme.mediaQueries.phablet]: {
                lineHeight: theme.lineHeights.loose,
              },
            })}
            dangerouslySetInnerHTML={{
              __html: text && text.childMarkdownRemark.html,
            }}
          />
        </div>
      )}
    </Fragment>
  )
}

Card.propTypes = {
  section: PropTypes.object.isRequired,
  index: PropTypes.number,
}

export default Card
